<template>
  <div ref="containerRef" class="address-autocomplete">
    <input
      ref="inputRef"
      :value="modelValue"
      class="field-input"
      :class="{ invalid: hasError }"
      type="text"
      :placeholder="placeholder"
      autocomplete="off"
      data-1p-ignore
      @input="onInput"
      @keydown="onKeydown"
      @focus="onFocus"
    />
    <ul
      v-if="isOpen && suggestions.length > 0"
      class="ac-dropdown"
      role="listbox"
    >
      <li
        v-for="(prediction, index) in suggestions"
        :key="prediction.place_id"
        class="ac-option"
        :class="{ 'ac-option-active': index === activeIndex }"
        role="option"
        :aria-selected="index === activeIndex"
        @mousedown.prevent="select(prediction)"
        @mousemove="activeIndex = index"
      >
        <i class="pi pi-map-marker ac-icon" />
        <span class="ac-label">{{ prediction.description }}</span>
      </li>
    </ul>
    <div v-if="isLoading" class="ac-loading" />
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core';

interface Prediction {
  description: string;
  place_id: string;
}

defineProps<{
  modelValue: string;
  confirmed: boolean;
  placeholder?: string;
  hasError?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'update:confirmed': [value: boolean];
}>();

const runtimeConfig = useRuntimeConfig();
const containerRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);
const suggestions = ref<Prediction[]>([]);
const isLoading = ref(false);
const isOpen = ref(false);
const activeIndex = ref(-1);
let sessiontoken = crypto.randomUUID();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

onClickOutside(containerRef, () => close());

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:modelValue', value);
  emit('update:confirmed', false);
  activeIndex.value = -1;

  if (debounceTimer) clearTimeout(debounceTimer);

  if (value.trim().length < 3) {
    suggestions.value = [];
    isOpen.value = false;
    return;
  }

  debounceTimer = setTimeout(() => search(value.trim()), 300);
}

function onFocus() {
  if (suggestions.value.length > 0) isOpen.value = true;
}

function onKeydown(event: KeyboardEvent) {
  if (!isOpen.value || suggestions.value.length === 0) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    activeIndex.value = Math.min(
      activeIndex.value + 1,
      suggestions.value.length - 1,
    );
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeIndex.value = Math.max(activeIndex.value - 1, 0);
  } else if (event.key === 'Enter') {
    if (activeIndex.value >= 0) {
      event.preventDefault();
      const suggestion = suggestions.value[activeIndex.value];
      if (suggestion) select(suggestion);
    }
  } else if (event.key === 'Escape') {
    close();
  }
}

async function search(query: string) {
  isLoading.value = true;
  try {
    const url = `${runtimeConfig.public.BACKEND_URL}/places/autocomplete?input=${encodeURIComponent(query)}&sessiontoken=${sessiontoken}`;
    const data = await $fetch<{ predictions: Prediction[] }>(url);
    suggestions.value = data.predictions ?? [];
    isOpen.value = suggestions.value.length > 0;
  } catch {
    suggestions.value = [];
    isOpen.value = false;
  } finally {
    isLoading.value = false;
  }
}

function select(prediction: Prediction) {
  emit('update:modelValue', prediction.description);
  emit('update:confirmed', true);
  sessiontoken = crypto.randomUUID();
  close();
}

function close() {
  isOpen.value = false;
  activeIndex.value = -1;
}
</script>

<style scoped>
.address-autocomplete {
  position: relative;
}

.ac-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  list-style: none;
  margin: 0;
  padding: 4px 0;
  z-index: 100;
  max-height: 240px;
  overflow-y: auto;
}

.ac-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  line-height: 1.4;
  color: var(--ink-dim);
  transition:
    background 0.08s,
    color 0.08s;
}

.ac-option:hover,
.ac-option-active {
  background: var(--line);
  color: var(--ink);
}

.ac-icon {
  font-size: 13px;
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--ink-dim);
}

.ac-label {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.ac-loading {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border: 2px solid var(--line);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}
</style>
